import { Injectable } from '@angular/core';
import { TableService } from './table.service';
import { OrderService } from './order.service';
import { BillingService } from './billing.service';
import { InsumosService } from './insumos.service';
import { DatabaseService } from './database.service';
import { Zone, Table } from '../models';
import { Insumo } from '../models/insumo.model';
import { TableStatus } from '../enums';
import { OrderStatus } from '../enums/order-status.enum';
import { OrderItemStatus } from '../enums/order-item-status.enum';
import { forkJoin, of } from 'rxjs';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DataInitService {
  constructor(
    private tableService: TableService,
    private orderService: OrderService,
    private billingService: BillingService,
    private insumosService: InsumosService,
    private database: DatabaseService
  ) {}

  initializeTestData(): void {
    // Esperar un poco para que IndexedDB esté listo
    setTimeout(() => {
      // Verificar si ya se inicializaron los datos en IndexedDB
      this.database.isInitialized().subscribe(isInitialized => {
        if (isInitialized) {
          console.log('Datos ya inicializados, cargando desde IndexedDB...');
          return;
        }

        console.log('Inicializando datos de prueba en IndexedDB...');
        this.initializeTables();
        this.initializeMenu();
        // Insumos y recetas después de que el menú exista
        setTimeout(() => this.initializeInsumosAndRecipes(), 1500);

        // En modo DEMO: crear pedidos y facturas de ejemplo para que el dashboard muestre datos
        if (environment.demo) {
          setTimeout(() => this.initializeDemoOrdersAndBills(), 2500);
        }

        // Marcar como inicializado después de un pequeño delay
        setTimeout(() => {
          this.database.setInitialized().subscribe({
            next: () => {
              console.log('Datos de prueba inicializados correctamente');
            },
            error: (error) => {
              console.error('Error marcando como inicializado:', error);
            }
          });
        }, environment.demo ? 4000 : 1000);
      });
    }, 500);
  }

  private initializeTables(): void {
    const zones = [
      { id: 'zone_0', name: 'Sala Principal', type: 'SALA' as const },
      { id: 'zone_1', name: 'Terraza', type: 'TERRAZA' as const },
      { id: 'zone_2', name: 'Sala Privada', type: 'VIP' as const }
    ];

    zones.forEach(zone => {
      const zoneObj = new Zone(zone.id, zone.name, zone.type);
      zoneObj.locationId = 'location_main'; // Sede Principal por defecto
      this.tableService.createZone(zoneObj);
      
      // Crear 6 mesas por zona (mismo código QR por defecto para imprimir y probar)
      const defaultAccessCode = 'demo-default';
      for (let i = 0; i < 6; i++) {
        const tableId = `table_${zone.id.split('_')[1]}_${i}`;
        const table = new Table(
          tableId,
          i + 1,
          4,
          zone.id,
          (i % 3) * 150 + 50,
          Math.floor(i / 3) * 150 + 50
        );
        table.status = TableStatus.AVAILABLE;
        table.accessCode = defaultAccessCode;
        this.tableService.createTable(table);
      }
    });
  }

  private initializeMenu(): void {
    // El menú ya se inicializa automáticamente en OrderService si no existe
    const existingMenu = this.orderService.getMenuItems();
    if (existingMenu.length === 0) {
      // El OrderService ya tiene initializeDefaultMenuItems que se ejecuta automáticamente
    }
  }

  /**
   * Insumos por defecto y recetas asociadas a los platos del menú (para pruebas).
   */
  private initializeInsumosAndRecipes(): void {
    this.database.getAllInsumos().subscribe(insumos => {
      const list = insumos || [];
      if (list.length > 0) return; // Ya hay insumos

      const defaultInsumos: Insumo[] = [
        new Insumo('insumo_1', 'Camarones', 'kg', 5),
        new Insumo('insumo_2', 'Limón', 'unidad', 50),
        new Insumo('insumo_3', 'Cebolla', 'kg', 3),
        new Insumo('insumo_4', 'Papa', 'kg', 10),
        new Insumo('insumo_5', 'Atún', 'kg', 2),
        new Insumo('insumo_6', 'Res', 'kg', 5),
        new Insumo('insumo_7', 'Pollo', 'kg', 5),
        new Insumo('insumo_8', 'Arroz', 'kg', 5),
        new Insumo('insumo_9', 'Mariscos', 'kg', 3),
        new Insumo('insumo_10', 'Ají amarillo', 'kg', 2),
        new Insumo('insumo_11', 'Huevos', 'unidad', 60),
        new Insumo('insumo_12', 'Leche', 'L', 5),
        new Insumo('insumo_13', 'Lúcuma', 'kg', 2),
        new Insumo('insumo_14', 'Maíz morado', 'kg', 2),
        new Insumo('insumo_15', 'Café', 'kg', 2),
      ];
      defaultInsumos.forEach(i => { i.minStock = i.unit === 'unidad' ? 10 : (i.unit === 'L' ? 1 : 0.5); });

      const serialized = defaultInsumos.map(i => ({
        id: i.id,
        name: i.name,
        unit: i.unit,
        quantity: i.quantity,
        minStock: i.minStock,
        active: true,
        createdAt: i.createdAt.toISOString(),
        updatedAt: i.updatedAt.toISOString(),
      }));

      const createInsumos$ = serialized.map(s => this.database.createInsumo(s));
      forkJoin(createInsumos$).pipe(
        catchError(err => { console.error('Error creando insumos:', err); return of([]); }),
        switchMap(() => {
          const recipes: { menuItemId: string; items: { insumoId: string; quantityPerUnit: number }[] }[] = [
            { menuItemId: 'item_1', items: [{ insumoId: 'insumo_1', quantityPerUnit: 0.15 }, { insumoId: 'insumo_2', quantityPerUnit: 2 }, { insumoId: 'insumo_3', quantityPerUnit: 0.05 }] },
            { menuItemId: 'item_2', items: [{ insumoId: 'insumo_4', quantityPerUnit: 0.2 }, { insumoId: 'insumo_5', quantityPerUnit: 0.08 }, { insumoId: 'insumo_2', quantityPerUnit: 0.5 }] },
            { menuItemId: 'item_3', items: [{ insumoId: 'insumo_6', quantityPerUnit: 0.15 }, { insumoId: 'insumo_2', quantityPerUnit: 0.3 }] },
            { menuItemId: 'item_4', items: [{ insumoId: 'insumo_6', quantityPerUnit: 0.2 }, { insumoId: 'insumo_4', quantityPerUnit: 0.15 }, { insumoId: 'insumo_3', quantityPerUnit: 0.05 }] },
            { menuItemId: 'item_5', items: [{ insumoId: 'insumo_7', quantityPerUnit: 0.15 }, { insumoId: 'insumo_10', quantityPerUnit: 0.03 }, { insumoId: 'insumo_8', quantityPerUnit: 0.05 }] },
            { menuItemId: 'item_6', items: [{ insumoId: 'insumo_9', quantityPerUnit: 0.2 }, { insumoId: 'insumo_2', quantityPerUnit: 2 }, { insumoId: 'insumo_3', quantityPerUnit: 0.05 }] },
            { menuItemId: 'item_7', items: [{ insumoId: 'insumo_7', quantityPerUnit: 0.12 }, { insumoId: 'insumo_8', quantityPerUnit: 0.08 }, { insumoId: 'insumo_3', quantityPerUnit: 0.03 }] },
            { menuItemId: 'item_8', items: [{ insumoId: 'insumo_11', quantityPerUnit: 2 }, { insumoId: 'insumo_12', quantityPerUnit: 0.15 }] },
            { menuItemId: 'item_9', items: [{ insumoId: 'insumo_13', quantityPerUnit: 0.1 }, { insumoId: 'insumo_12', quantityPerUnit: 0.1 }] },
            { menuItemId: 'item_10', items: [{ insumoId: 'insumo_14', quantityPerUnit: 0.05 }] },
            { menuItemId: 'item_11', items: [{ insumoId: 'insumo_15', quantityPerUnit: 0.01 }] },
          ];
          const setRecipes$ = recipes.map(r => this.database.setRecipe(r.menuItemId, r.items));
          return forkJoin(setRecipes$);
        }),
        catchError(err => { console.error('Error asignando recetas:', err); return of([]); })
      ).subscribe(() => {
        this.insumosService.reloadInsumosFromDb();
        console.log('Insumos y recetas por defecto inicializados.');
      });
    });
  }

  /**
   * Crea pedidos y facturas de ejemplo para el modo DEMO (dashboard con datos desde el inicio).
   * Solo se ejecuta cuando environment.demo es true.
   */
  private initializeDemoOrdersAndBills(): void {
    this.database.getAllOrders().subscribe(existingOrders => {
      if (existingOrders && existingOrders.length > 0) {
        console.log('Ya existen pedidos, no se crean datos de demo.');
        return;
      }

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const iso = (d: Date) => d.toISOString();

      // Precios del menú por defecto (item_1..item_11)
      const menuPrices: { [id: string]: number } = {
        item_1: 28500, item_2: 18500, item_3: 22000, item_4: 35000, item_5: 28000,
        item_6: 32000, item_7: 24500, item_8: 12000, item_9: 9500, item_10: 5500, item_11: 4500
      };

      const makeItem = (orderId: string, idx: number, menuItemId: string, qty: number) => {
        const price = menuPrices[menuItemId] || 10000;
        const subtotal = price * qty;
        const itemId = `${orderId}_item_${idx}`;
        return {
          id: itemId,
          menuItemId,
          quantity: qty,
          status: OrderItemStatus.DELIVERED,
          notes: null,
          price,
          subtotal,
          createdAt: iso(now),
          updatedAt: iso(now)
        };
      };

      const orders: any[] = [
        {
          id: 'order_demo_1',
          tableId: 'table_0_0',
          linkedTableIds: [],
          status: OrderStatus.CONFIRMED,
          items: [
            makeItem('order_demo_1', 0, 'item_1', 1),
            makeItem('order_demo_1', 1, 'item_4', 1),
            makeItem('order_demo_1', 2, 'item_11', 2)
          ],
          totalAmount: 28500 + 35000 + 9000,
          tax: 11540,
          discount: 0,
          notes: null,
          createdAt: iso(now),
          confirmedAt: iso(now),
          updatedAt: iso(now)
        },
        {
          id: 'order_demo_2',
          tableId: 'table_0_1',
          linkedTableIds: [],
          status: OrderStatus.IN_KITCHEN,
          items: [
            makeItem('order_demo_2', 0, 'item_2', 2),
            makeItem('order_demo_2', 1, 'item_5', 1),
            makeItem('order_demo_2', 2, 'item_10', 2)
          ],
          totalAmount: 37000 + 28000 + 11000,
          tax: 12160,
          discount: 0,
          notes: null,
          createdAt: iso(now),
          confirmedAt: iso(now),
          updatedAt: iso(now)
        },
        {
          id: 'order_demo_3',
          tableId: 'table_1_0',
          linkedTableIds: [],
          status: OrderStatus.READY_TO_SERVE,
          items: [
            makeItem('order_demo_3', 0, 'item_6', 1),
            makeItem('order_demo_3', 1, 'item_8', 1)
          ],
          totalAmount: 32000 + 12000,
          tax: 7040,
          discount: 0,
          notes: null,
          createdAt: iso(now),
          confirmedAt: iso(now),
          updatedAt: iso(now)
        },
        {
          id: 'order_demo_4',
          tableId: 'table_1_1',
          linkedTableIds: [],
          status: OrderStatus.COMPLETED,
          items: [
            makeItem('order_demo_4', 0, 'item_3', 2),
            makeItem('order_demo_4', 1, 'item_9', 1)
          ],
          totalAmount: 44000 + 9500,
          tax: 8560,
          discount: 0,
          notes: null,
          createdAt: iso(now),
          confirmedAt: iso(now),
          completedAt: iso(now),
          updatedAt: iso(now)
        },
        {
          id: 'order_demo_5',
          tableId: 'table_1_2',
          linkedTableIds: [],
          status: OrderStatus.INVOICED,
          items: [
            makeItem('order_demo_5', 0, 'item_7', 1),
            makeItem('order_demo_5', 1, 'item_4', 1)
          ],
          totalAmount: 24500 + 35000,
          tax: 9520,
          discount: 2000,
          notes: null,
          createdAt: iso(now),
          confirmedAt: iso(now),
          completedAt: iso(now),
          updatedAt: iso(now)
        },
        {
          id: 'order_demo_6',
          tableId: 'table_2_0',
          linkedTableIds: [],
          status: OrderStatus.INVOICED,
          items: [
            makeItem('order_demo_6', 0, 'item_1', 2),
            makeItem('order_demo_6', 1, 'item_10', 3)
          ],
          totalAmount: 57000 + 16500,
          tax: 11760,
          discount: 0,
          notes: null,
          createdAt: iso(now),
          confirmedAt: iso(now),
          completedAt: iso(now),
          updatedAt: iso(now)
        }
      ];

      orders.forEach(o => {
        o.totalAmount = o.items.reduce((s: number, i: any) => s + i.subtotal, 0);
        o.tax = Math.round(o.totalAmount * 0.16);
        if (!o.discount) o.discount = 0;
      });

      const createOrder$ = orders.map(ord => this.database.createOrder(ord));
      forkJoin(createOrder$).subscribe({
        next: () => {
          console.log('Pedidos de demo creados.');
          this.orderService.reloadOrdersFromDb();
          // Marcar mesas con pedidos como ocupadas
          const tableIdsWithOrders = ['table_0_0', 'table_0_1', 'table_1_0', 'table_1_1', 'table_1_2', 'table_2_0'];
          tableIdsWithOrders.forEach(id => this.tableService.updateTableStatus(id, TableStatus.OCCUPIED));

          // Facturas de ejemplo (hoy) para que Ingresos Hoy y Reportes tengan datos
          const bills: any[] = [
            {
              id: 'bill_demo_1',
              orderId: 'order_demo_5',
              tableId: 'table_1_2',
              orderIds: ['order_demo_5'],
              linkedTableIds: [],
              subtotal: 59500,
              tax: 9520,
              discount: 2000,
              total: 67020,
              paymentMethod: 'CARD',
              tip: 3000,
              notes: null,
              status: 'FINALIZED',
              createdAt: iso(today),
              paidAt: iso(now),
              dian: {
                cufe: 'DEMO-CUFE-' + Date.now(),
                resolutionNumber: '18764000000001',
                invoicePrefix: 'FE',
                invoiceNumber: '1001',
                authorizationDate: iso(today)
              }
            },
            {
              id: 'bill_demo_2',
              orderId: 'order_demo_6',
              tableId: 'table_2_0',
              orderIds: ['order_demo_6'],
              linkedTableIds: [],
              subtotal: 73500,
              tax: 11760,
              discount: 0,
              total: 85260,
              paymentMethod: 'CASH',
              tip: 0,
              notes: null,
              status: 'FINALIZED',
              createdAt: iso(today),
              paidAt: iso(now),
              dian: {
                cufe: 'DEMO-CUFE-' + (Date.now() + 1),
                resolutionNumber: '18764000000001',
                invoicePrefix: 'FE',
                invoiceNumber: '1002',
                authorizationDate: iso(today)
              }
            },
            {
              id: 'bill_demo_3',
              orderId: 'order_demo_4',
              tableId: 'table_1_1',
              orderIds: ['order_demo_4'],
              linkedTableIds: [],
              subtotal: 53500,
              tax: 8560,
              discount: 0,
              total: 62060,
              paymentMethod: 'DIGITAL_WALLET',
              tip: 2000,
              notes: null,
              status: 'FINALIZED',
              createdAt: iso(today),
              paidAt: iso(now),
              dian: {
                cufe: 'DEMO-CUFE-' + (Date.now() + 2),
                resolutionNumber: '18764000000001',
                invoicePrefix: 'FE',
                invoiceNumber: '1003',
                authorizationDate: iso(today)
              }
            }
          ];

          const createBill$ = bills.map(b => this.database.createBill(b));
          forkJoin(createBill$).subscribe({
            next: () => {
              console.log('Facturas de demo creadas.');
              this.billingService.reloadBillsFromDb();
            },
            error: err => console.error('Error creando facturas de demo:', err)
          });
        },
        error: err => console.error('Error creando pedidos de demo:', err)
      });
    });
  }

  resetData(): void {
    // Limpiar todos los datos de IndexedDB
    this.database.clearAll().subscribe({
      next: () => {
        // Resetear los subjects
        this.tableService.resetData();
        this.orderService.resetData();
        this.billingService.resetData();
        
        // Reinicializar datos
        setTimeout(() => {
          this.initializeTestData();
        }, 500);
      },
      error: (error) => {
        console.error('Error resetting data:', error);
      }
    });
  }
}
